import React from 'react';

import { withEventScope } from '@repo/event-processor';
import type { PromoCode } from '@repo/subscription/api';

import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../stores/planSelectionStore';
import { formatMonetaryValue } from '../../../utils/billing';
import { NextChargeData } from '../hooks/types';

import { PromoCodeHighlight, TableRow } from './styles';

/**
 * Formats the promo code discount amount and displays in percentage or in actual value.
 */
const formatPromoCodeLabel = (
  promoCode: PromoCode,
  nextCharge: NextChargeData
): string => {
  const nextChargeValue = nextCharge.value;
  const off = promoCode.amountOff
    ? formatMonetaryValue(
        {
          ...nextChargeValue,
          amount: Math.min(promoCode.amountOff, nextChargeValue.amount),
        },
        { fractionDigits: 0 }
      )
    : `${promoCode.percentOff}%`;

  return `Promo code -${off} off`;
};

const formatPromoCodeDiscountAmount = (nextCharge: NextChargeData): string => {
  const nextChargeValue = nextCharge.value;
  const value = {
    ...nextChargeValue,
    amount: nextChargeValue.originalAmount - nextChargeValue.amount,
  };

  return `- ${formatMonetaryValue(value)}`;
};

/**
 * This component displays the details of the applied promo code for a subscription, including the promo code label and discount amount.
 */
const PromoCodeSection: React.FC<{ nextCharge: NextChargeData }> = ({
  nextCharge,
}) => {
  const promoCode = usePlanSelectionStore(planSelectionStoreSelector.promoCode);

  if (!promoCode) {
    return null;
  }

  return (
    <TableRow>
      <PromoCodeHighlight>
        {formatPromoCodeLabel(promoCode, nextCharge)}
      </PromoCodeHighlight>
      <PromoCodeHighlight style={{ textAlign: 'right' }}>
        {formatPromoCodeDiscountAmount(nextCharge)}
      </PromoCodeHighlight>
    </TableRow>
  );
};

export default withEventScope(PromoCodeSection, { module: 'promoCodeSection' });
